import { Link } from "@tanstack/react-router";
import { Section, Eyebrow, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { practiceAreas, type PracticeArea } from "@/data/practice-areas";

/* ---------- 1. Trust bar ---------- */
export function TrustBar({ shortName }: { shortName: string }) {
  const badges = [
    { label: "Clutch Top SEO", sub: "Legal industry leader" },
    { label: "G2 High Performer", sub: "2025 winter cohort" },
    { label: "12+ years", sub: "ranking law firms" },
  ];
  return (
    <div className="border-y border-border bg-background">
      <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-2 md:grid-cols-5 gap-6 items-center">
        {badges.map((b) => (
          <div key={b.label} className="text-center md:text-left">
            <div className="text-sm font-semibold text-foreground">{b.label}</div>
            <div className="text-xs text-muted-foreground mt-1">{b.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- 2. Why this niche is different (E-E-A-T) ---------- */
export function WhyDifferent({ shortName }: { shortName: string }) {
  const s = shortName;
  const items = [
    { t: "Bar-compliance built in", d: `Every ${s.toLowerCase()} page, ad, and schema block passes an ABA Model Rule 7 + state bar advertising review before it goes live.` },
    { t: `${s}-specific schema`, d: `We deploy LegalService, Attorney, EmergencyService (where allowed), FAQPage, and Review markup tuned for the ${s.toLowerCase()} search intent, not generic law firm boilerplate.` },
    { t: "Reputation & suppression", d: `Aggressive brand-protection assets to bury mugshot sites, complaint pages, and lead-gen resellers hijacking your ${s.toLowerCase()} firm's name.` },
    { t: "Case-economics driven", d: `We optimize for the case types that actually pay, not vanity keywords. Every ${s.toLowerCase()} content brief is tied to a target case value.` },
  ];
  return (
    <Section>
      <Eyebrow>Why {s} SEO is different</Eyebrow>
      <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">Generic law firm SEO loses {s.toLowerCase()} cases. Here's what actually works.</h2>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {items.map((it) => (
          <div key={it.t} className="rounded-xl border border-border bg-card p-6">
            <div className="text-lg font-semibold text-foreground">{it.t}</div>
            <p className="mt-2 text-muted-foreground leading-relaxed">{it.d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------- 3. Process timeline ---------- */
export function ProcessTimeline({ shortName, title, steps: customSteps }: { shortName: string; title?: string; steps?: { n: string; t: string; d: string }[] }) {
  const steps = customSteps ?? [
    { n: "01", t: "Audit", d: `Full technical, on-page, GBP, backlink, and competitor audit specific to your ${shortName.toLowerCase()} market. Delivered in 10 business days.` },
    { n: "02", t: "Strategy", d: `Custom 12-month roadmap with keyword map, content calendar, link targets, and monthly KPIs tied to signed cases.` },
    { n: "03", t: "Build", d: `We ship city + practice pages, schema, site speed fixes, and attorney-reviewed content weekly.` },
    { n: "04", t: "Rank", d: `Ongoing on-page, editorial link acquisition, GBP posts, and review velocity, all measured against the roadmap.` },
    { n: "05", t: "Report", d: `Monthly plain-English reporting: rankings, calls, forms, consultations, signed cases. Quarterly strategy review with your senior strategist.` },
  ];
  return (
    <div className="bg-secondary/40 border-y border-border">
      <Section>
        <Eyebrow>How we work</Eyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">{title ?? "A transparent, 5-step engagement, no surprises."}</h2>
        <div className="mt-10 grid md:grid-cols-5 gap-4">
          {steps.map((s) => (
            <div key={s.n} className="rounded-xl border border-border bg-background p-6">
              <div className="font-display text-3xl text-[var(--gold-deep)] font-semibold">{s.n}</div>
              {customSteps ? <h3 className="mt-2 text-lg font-semibold text-foreground">{s.t}</h3> : <div className="mt-2 text-lg font-semibold text-foreground">{s.t}</div>}
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ---------- 4. Testimonials carousel (static scroll strip) ---------- */
export function Testimonials({ shortName }: { shortName: string }) {
  const items = [
    { q: `Within 7 months our ${shortName.toLowerCase()} intake doubled. The reporting alone is worth the retainer.`, n: "Managing Partner", f: "Boutique litigation firm", c: "Houston, TX" },
    { q: `They understand contingency-fee economics. Every content brief is tied to the case types we actually want.`, n: "Founding Attorney", f: "Trial firm", c: "Phoenix, AZ" },
    { q: `We stopped burning $18k/month on Google Ads. Organic now delivers 70% of our signed cases.`, n: "Marketing Director", f: "Multi-office firm", c: "Chicago, IL" },
    { q: `Best decision we made in a decade of running this practice. Ranked #1 in our county for our top three keywords.`, n: "Equity Partner", f: "Regional firm", c: "Boston, MA" },
  ];
  return (
    <Section>
      <Eyebrow>What clients say</Eyebrow>
      <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">Attorneys don't hire on promises. They hire on proof.</h2>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {items.map((t) => (
          <figure key={t.q} className="rounded-xl border border-border bg-card p-6">
            <blockquote className="text-lg text-foreground leading-relaxed">&ldquo;{t.q}&rdquo;</blockquote>
            <figcaption className="mt-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{t.n}</span>, {t.f} &middot; {t.c}
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

/* ---------- 5. SEO vs Ads comparison ---------- */
export function SeoVsAds({ shortName, rows: customRows, embedded = false }: { shortName: string; rows?: { k: string; seo: string; ads: string }[]; embedded?: boolean }) {
  const rows = customRows ?? [
    { k: "Typical cost per click", seo: "$0 (organic)", ads: `$40–$200 for ${shortName.toLowerCase()} keywords` },
    { k: "Cost per signed case", seo: "$150–$600 at scale", ads: "$900–$3,500" },
    { k: "Time to first results", seo: "60–120 days", ads: "24 hours" },
    { k: "12-month compounding", seo: "Yes, traffic grows monthly", ads: "No, spend stops = leads stop" },
    { k: "Trust signal to prospects", seo: "High, top of natural results", ads: "Lower, marked as 'Sponsored'" },
    { k: "Owned by your firm", seo: "Yes, rankings & content are yours", ads: "No, it's rented traffic" },
  ];
  const table = <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-background overscroll-x-contain sm:mt-10">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-secondary/60 text-foreground">
              <tr>
                <th className="p-4 font-semibold">Metric</th>
                <th className="p-4 font-semibold">SEO</th>
                <th className="p-4 font-semibold">Google Ads</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.k} className="border-t border-border">
                  <td className="p-4 font-medium text-foreground">{r.k}</td>
                  <td className="p-4 text-foreground">{r.seo}</td>
                  <td className="p-4 text-muted-foreground">{r.ads}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>;
  if (embedded) return table;
  return (
    <div className="bg-secondary/40 border-y border-border">
      <Section>
        <Eyebrow>SEO vs. Google Ads</Eyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">Why {shortName.toLowerCase()} firms shift budget from Ads to SEO.</h2>
        {table}
      </Section>
    </div>
  );
}

/* ---------- 6. Pricing / investment framing ---------- */
export function InvestmentSection({ shortName }: { shortName: string }) {
  const tiers = [
    { name: "Local", price: "$1,500–$2,500 / mo", d: `Single office, single ${shortName.toLowerCase()} market. GBP, on-page, monthly content, review velocity.` },
    { name: "Growth", price: "$3,500–$6,000 / mo", d: `Multi-city or multi-practice ${shortName.toLowerCase()} firms. Full content velocity, editorial links, technical SEO.` },
    { name: "Enterprise", price: "$7,500–$12,000+ / mo", d: `Multi-office, multi-state ${shortName.toLowerCase()} programs with digital PR, dedicated strategist, and custom reporting.` },
  ];
  return (
    <Section>
      <Eyebrow>Investment</Eyebrow>
      <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">What {shortName.toLowerCase()} SEO typically costs, and why.</h2>
      <p className="mt-4 max-w-3xl text-muted-foreground text-lg">Law firm SEO retainers industry-wide run $1,500 to $10,000+ per month depending on market competition, practice area, and content velocity. We publish rough ranges so you can qualify us before a call.</p>
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {tiers.map((t) => (
          <div key={t.name} className="rounded-xl border border-border bg-card p-6">
            <div className="text-sm uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{t.name}</div>
            <div className="mt-2 font-display text-2xl text-foreground font-semibold">{t.price}</div>
            <p className="mt-3 text-muted-foreground leading-relaxed">{t.d}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Every quote is custom. Ranges reflect typical engagements, not a fixed price list.</p>
    </Section>
  );
}

/* ---------- 7. Who this is for / not for ---------- */
export function WhoThisIsFor({ shortName, title, budgetItem }: { shortName: string; title?: string; budgetItem?: string }) {
  const good = [
    `Established ${shortName.toLowerCase()} firms ready to invest 6–12 months in compounding organic growth`,
    budgetItem ?? "Firms with $2k+/month marketing budget and clear case economics",
    "Practices willing to have attorneys review content for accuracy",
    "Firms that value one-per-market exclusivity",
  ];
  const bad = [
    "Firms expecting page-one rankings in 30 days",
    "Practices unwilling to allocate attorney time for content review",
    "Firms already working with a competitor of ours in the same market",
    "Firms looking for lowest-bidder, hands-off SEO",
  ];
  return (
    <div className="bg-secondary/40 border-y border-border">
      <Section>
        <Eyebrow>Fit check</Eyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-foreground max-w-3xl">{title ?? "Who this is for, and who it isn't."}</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="rounded-xl border border-border bg-background p-6">
            <div className="text-lg font-semibold text-foreground">Great fit</div>
            <ul className="mt-4 space-y-3">
              {good.map((g) => (
                <li key={g} className="flex gap-3"><span className="text-[var(--gold-deep)] font-bold">✓</span><span className="text-foreground">{g}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-background p-6">
            <div className="text-lg font-semibold text-foreground">Not a fit</div>
            <ul className="mt-4 space-y-3">
              {bad.map((b) => (
                <li key={b} className="flex gap-3"><span className="text-destructive">✗</span><span className="text-muted-foreground">{b}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ---------- 8. Expanded FAQ helper ---------- */
export function expandFaqs(area: PracticeArea) {
  const s = area.shortName;
  const extras = [
    { q: `How long until a ${s.toLowerCase()} firm sees ranking movement?`, a: `Most ${s.toLowerCase()} firms see local pack and long-tail movement in 60–120 days, with primary metro keywords ranking between months 6–12 depending on competition.` },
    { q: "What contract length do you require?", a: "Engagements are 12 months because SEO compounds over that horizon. There is no auto-renewal, no cancellation penalty at renewal, and month-to-month conversion is available after month 12." },
    { q: "Who owns the content, links, and site changes?", a: "You do, from day one. All content, backlinks, tracking, and site changes are the client's property. If we part ways, everything stays with your firm." },
    { q: "How does reporting work?", a: "Monthly reports include rankings, organic traffic, tracked calls, form submissions, consultations booked, and signed cases, with a plain-English summary a partner can read in five minutes. Quarterly strategy reviews are led by your senior strategist." },
    { q: "What happens if rankings drop after a Google update?", a: `We monitor every core update and roll out a recovery plan within 10 business days, at no extra cost. Our ${s.toLowerCase()} portfolio has weathered every major update since 2018.` },
    { q: `Do you work with more than one ${s.toLowerCase()} firm in the same city?`, a: `No. We work with one ${s.toLowerCase()} firm per metro market. If we take your ${s.toLowerCase()} practice in a city, we won't take a competitor's, that exclusivity is written into every engagement.` },
    { q: "What's included versus billed as an add-on?", a: "Strategy, technical SEO, on-page, content, GBP, link acquisition, reviews, and reporting are all included in every retainer. Video production, paid media management, and full site rebuilds are quoted separately." },
    { q: "Are you bar-compliant?", a: "Yes. Every published asset passes an ABA Model Rule 7 + state bar advertising review, and we keep a compliance log tied to every page and ad." },
  ];
  const seen = new Set(area.faqs.map((f) => f.q.toLowerCase()));
  const merged = [...area.faqs];
  for (const e of extras) {
    if (!seen.has(e.q.toLowerCase())) merged.push(e);
    if (merged.length >= 10) break;
  }
  // ensure minimum 10
  for (const e of extras) {
    if (merged.length >= 10) break;
    if (!merged.find((m) => m.q.toLowerCase() === e.q.toLowerCase())) merged.push(e);
  }
  return merged;
}

/* ---------- Inline CTA banner (contextual, mid-page) ---------- */
export function InlineCTA({
  headline,
  sub,
  primaryLabel,
  primaryTo = "/law-firm-seo-audit",
  secondaryLabel = "Talk to a strategist",
  secondaryTo = BOOKING_URL,
  tone = "light",
}: {
  headline: string;
  sub?: string;
  primaryLabel: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  tone?: "light" | "dark";
}) {
  const dark = tone === "dark";
  return (
    <div className={dark ? "bg-hero-gradient text-primary-foreground" : "bg-background"}>
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-6 md:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-y border-border">
        <div className="max-w-2xl">
          <h3 className={`text-2xl md:text-3xl font-semibold ${dark ? "text-primary-foreground" : "text-foreground"}`}>{headline}</h3>
          {sub && <p className={`mt-2 ${dark ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{sub}</p>}
        </div>
        <div className="flex w-full flex-col gap-3 flex-shrink-0 sm:w-auto sm:flex-row sm:flex-wrap">
          <CTAButton to={primaryTo} variant="gold">{primaryLabel}</CTAButton>
          <CTAButton to={secondaryTo} variant={dark ? "ghostLight" : "ghostLight"}>{secondaryLabel}</CTAButton>
        </div>
      </div>
    </div>
  );
}

/* ---------- Cross-link block reused ---------- */
export function OtherPracticeAreas({ currentSlug }: { currentSlug: string }) {
  return (
    <Section>
      <h3 className="text-2xl font-semibold mb-6">Other practice areas we rank</h3>
      <div className="flex flex-wrap gap-2">
        {practiceAreas.filter((p) => p.slug !== currentSlug).map((p) => (
          <Link key={p.slug} to="/practice-areas/$slug" params={{ slug: p.slug }} className="rounded-full border border-border px-4 py-2 text-sm hover:border-[var(--gold)] hover:text-[var(--gold-deep)] transition">{p.shortName}</Link>
        ))}
      </div>
    </Section>
  );
}

/* ---------- Investment CTA row ---------- */
export function QuickCTARow({ shortName }: { shortName: string }) {
  return (
    <div className="mx-auto max-w-7xl px-6 pb-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <CTAButton to="/law-firm-seo-audit" variant="gold">Get a free {shortName} audit</CTAButton>
        <CTAButton to="/pricing" variant="ghostLight">See detailed pricing</CTAButton>
      </div>
    </div>
  );
}